export type ConsentCategory = 'analytics' | 'sessionReplay';

export interface ConsentPreferences {
  v: 1;
  ts: string;
  categories: Record<ConsentCategory, boolean>;
}

const COOKIE_NAME = 'referee_consent';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

function isNativePlatform(): boolean {
  return typeof window !== 'undefined' && 'Capacitor' in window && !!(window as Record<string, unknown>).Capacitor;
}

function getCookieDomain(): string {
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('.referee.app')) {
    return '; domain=.referee.app';
  }
  return '';
}

export function readConsent(): ConsentPreferences | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = isNativePlatform()
      ? localStorage.getItem(COOKIE_NAME)
      : document.cookie
          .split('; ')
          .find((c) => c.startsWith(`${COOKIE_NAME}=`))
          ?.split('=')[1];

    if (!raw) {
      return null;
    }

    const value = decodeURIComponent(raw);
    const parsed: unknown = JSON.parse(value);

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'v' in parsed &&
      (parsed as ConsentPreferences).v === 1 &&
      'categories' in parsed &&
      typeof (parsed as ConsentPreferences).categories === 'object'
    ) {
      return parsed as ConsentPreferences;
    }

    return null;
  } catch {
    return null;
  }
}

export function writeConsent(
  categories: Record<ConsentCategory, boolean>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const preferences: ConsentPreferences = {
    v: 1,
    ts: new Date().toISOString(),
    categories,
  };

  const value = encodeURIComponent(JSON.stringify(preferences));

  if (isNativePlatform()) {
    localStorage.setItem(COOKIE_NAME, value);
  } else {
    document.cookie = `${COOKIE_NAME}=${value}; max-age=${COOKIE_MAX_AGE}; path=/${getCookieDomain()}; SameSite=Strict`;
  }
}

export function hasConsent(category: ConsentCategory): boolean {
  const prefs = readConsent();
  return prefs?.categories[category] === true;
}
